import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, ArrowRight, Phone, Mail, MapPin, Instagram, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('1');
  const location = useLocation();

  const handleCheckAvailability = () => {
    if (!checkIn || !checkOut) {
      alert("Please select both check-in and check-out dates.");
      return;
    }

    if (checkOut <= checkIn) {
      alert("Check-out date must be after the check-in date.");
      return;
    }

    const params = new URLSearchParams({
      checkIn,
      checkOut,
      guests,
    }).toString();

    setIsOpen(false)
    navigate(`/room-booking?${params}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      const isRoomBookingPage = location.pathname === "/room-booking";
      const isRoomBookingConfirmed = location.pathname === "/booking/confirmation";
      const isCheckoutPage = location.pathname === "/booking/checkout"; // add checkout page check

      if (isRoomBookingPage || isCheckoutPage || isRoomBookingConfirmed || window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    handleScroll(); // run once on mount
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);


  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // Reset submenu state when main menu toggles
    if (!isOpen) {
      setActiveSubmenu(null);
    }
  };

  const toggleSubmenu = (menu) => {
    setActiveSubmenu(activeSubmenu === menu ? null : menu);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setActiveSubmenu(null);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  }

  return (
    <nav className={`font-serif fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-black shadow-md py-2 text-white' : 'bg-transparent py-4 text-white'}`}>
      {/* Desktop Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to={"/"}>
              <img
                src="https://ik.imagekit.io/tdlebsr5e/TheGulmoharGrand.jpeg?updatedAt=1750152666297"
                alt="Gulmohar Grand Hotel"
                className="h-16 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="hover:text-amber-700 font-medium transition duration-300">Home</Link>
            <Link to="/about" className="hover:text-amber-700 font-medium transition duration-300">About Us</Link>
            <Link to="/gallery" className="hover:text-amber-700 font-medium transition duration-300">Gallery</Link>
            <div className="relative group">
              <button
                className="flex items-center text-white hover:text-amber-700 font-medium transition duration-300"
                onClick={toggleDropdown}
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                Our Rooms <ChevronDown size={16} className="ml-1" />
              </button>
              <div
                className={`absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg transition-all duration-200 ${dropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <Link to="/room-booking?type=deluxe" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Deluxe Rooms</Link>
                <Link to="/room-booking?type=executive" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Executive Suite</Link>
                <Link to="/room-booking?type=premium" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Premium Room</Link>
                <Link to="/room-booking?type=suite" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Presidential Suite</Link>
              </div>
            </div>
            <Link to="/blog" className="hover:text-amber-700 font-medium transition duration-300">Blog</Link>
            <Link to="/contact" className="hover:text-amber-700 font-medium transition duration-300">Contact us</Link>
            <Link to="/room-booking" className="bg-gradient-to-r from-amber-600 to-amber-500 text-white px-6 py-2.5 rounded-md font-medium hover:from-amber-700 hover:to-amber-600 transition duration-300 shadow-md">Book Now</Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className={`${scrolled ? 'text-white' : 'text-white'} hover:text-amber-600 focus:outline-none transition duration-300`}
              aria-label="Toggle menu"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu - Bottom Sheet Style */}
      <div
        className={`md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-500 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={closeMenu}
      >
        <div
          className={`fixed bottom-0 inset-x-0 bg-vlack rounded-t-3xl transform transition-transform duration-500 ease-in-out ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
          style={{ maxHeight: '90vh', overflowY: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="w-full flex justify-center pt-3 pb-1">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>

          {/* Menu Header */}
          <div className="px-6 py-4 flex justify-between items-center border-b border-gray-100">
            <h2 className="text-xl font-medium text-amber-500">Menu</h2>
            <button
              onClick={closeMenu}
              className="text-gray-400 hover:text-amber-600 focus:outline-none transition duration-300"
            >
              <X size={24} />
            </button>
          </div>

          {/* Navigation Links */}
          <div className="px-6 py-4 space-y-1">
            <Link
              to="/"
              className="block py-3.5 px-4 text-white hover:bg-amber-50 hover:text-amber-800 rounded-lg transition duration-300 border-b border-gray-50"
              onClick={closeMenu}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block py-3.5 px-4 text-white hover:bg-amber-50 hover:text-amber-800 rounded-lg transition duration-300 border-b border-gray-50"
              onClick={closeMenu}
            >
              About Us
            </Link>
            <Link
              to="/gallery"
              className="block py-3.5 px-4 text-white hover:bg-amber-50 hover:text-amber-800 rounded-lg transition duration-300 border-b border-gray-50"
              onClick={closeMenu}
            >
              Gallery
            </Link>

            {/* Rooms submenu in mobile view */}
            <div className="border-b border-gray-50">
              <button
                onClick={() => toggleSubmenu('rooms')}
                className="w-full flex items-center justify-between py-3.5 px-4 text-white hover:bg-amber-50 hover:text-amber-800 rounded-lg transition duration-300"
              >
                <span>Our Rooms</span>
                <ChevronDown
                  size={18}
                  className={`text-amber-700 transition-transform duration-300 ${activeSubmenu === 'rooms' ? 'rotate-180' : ''}`}
                />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${activeSubmenu === 'rooms' ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="py-2 bg-amber-50/50 rounded-lg mx-2 my-1">
                  <Link
                    to="/rooms/deluxe"
                    className="flex items-center py-2.5 px-8 text-white hover:text-amber-800 transition duration-300"
                    onClick={closeMenu}
                  >
                    <ArrowRight size={14} className="mr-2 text-amber-600" />
                    <Link to="/room-booking?type=deluxe" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Deluxe Rooms</Link>
                  </Link>
                  <Link
                    to="/rooms/executive"
                    className="flex items-center py-2.5 px-8 text-white hover:text-amber-800 transition duration-300"
                    onClick={closeMenu}
                  >
                    <ArrowRight size={14} className="mr-2 text-amber-600" />
                    <Link to="/room-booking?type=premier" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Executive Suite</Link>
                  </Link>
                  <Link
                    to="/rooms/premium"
                    className="flex items-center py-2.5 px-8 text-white hover:text-amber-800 transition duration-300"
                    onClick={closeMenu}
                  >
                    <ArrowRight size={14} className="mr-2 text-amber-600" />
                    <Link to="/room-booking?type=premier" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Premium Room</Link>
                  </Link>
                  <Link
                    to="/rooms/presidential"
                    className="flex items-center py-2.5 px-8 text-white hover:text-amber-800 transition duration-300"
                    onClick={closeMenu}
                  >
                    <ArrowRight size={14} className="mr-2 text-amber-600" />
                    <Link to="/room-booking?type=suite" className="block px-4 py-2 text-gray-800 hover:bg-amber-50 hover:text-amber-700 transition duration-300">Presidential Suite</Link>
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/blog"
              className="block py-3.5 px-4 text-white hover:bg-amber-50 hover:text-amber-800 rounded-lg transition duration-300 border-b border-gray-50"
              onClick={closeMenu}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className="block py-3.5 px-4 text-white hover:bg-amber-50 hover:text-amber-800 rounded-lg transition duration-300 border-b border-gray-50"
              onClick={closeMenu}
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile Check Availability Form */}
          <div className="block md:hidden px-6 py-4">
            <h3 className="text-white text-lg font-semibold mb-4">Check Availability</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-white text-sm mb-1">Check-In</label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => {
                    setCheckIn(e.target.value);
                    // Reset check-out if it's before new check-in
                    if (checkOut && e.target.value >= checkOut) {
                      setCheckOut("");
                    }
                  }}
                  className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-white text-sm mb-1">Check-Out</label>
                <input
                  type="date"
                  value={checkOut}
                  min={checkIn || undefined} // Prevent selecting before check-in
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-white text-sm mb-1">Guests</label>
                <select
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className="w-full bg-white text-black px-4 py-2 rounded-md focus:outline-none"
                >
                  <option value="1">1 Guest</option>
                  <option value="2">2 Guests</option>
                  <option value="3">3 Guests</option>
                  <option value="4">4 Guests</option>
                </select>
              </div>
              <div>
                <button
                  onClick={handleCheckAvailability}
                  className="w-full bg-amber-50/30 hover:bg-amber-700 text-white py-3 rounded-md uppercase font-medium tracking-widest transition-all"
                >
                  Check Availability
                </button>
              </div>
            </div>
          </div>


          {/* Book Now Button */}
          <div className="px-6 py-4">
            <Link
              to="/room-booking"
              className="block w-full py-4 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-center text-white rounded-lg font-medium hover:bg-amber-700 transition duration-300 shadow-md"
              onClick={closeMenu}
            >
              Book Your Stay Now
            </Link>
          </div>

          {/* Contact Information */}
          <div className="px-6 py-4 bg-black">
            <h3 className="font-medium text-amber-600 mb-4">Contact Information</h3>

            <div className="grid grid-cols-1 gap-4">
              <a href="tel:+916230562306" className="flex items-center text-white hover:text-amber-800 transition duration-300">
                <Phone size={16} className="mr-3 text-amber-600" />
                <span>+91 62305 62306</span>
              </a>

              <a href="mailto:info@gulmohargrandhotel.com" className="flex items-center text-white hover:text-amber-800 transition duration-300">
                <Mail size={16} className="mr-3 text-amber-600" />
                <span>info@gulmohargrandhotel.com</span>
              </a>

              <div className="flex items-start text-white">
                <MapPin size={16} className="mr-3 mt-1 text-amber-600" />
                <span>8 Miles Stone, Behdala, Una, HP</span>
              </div>
            </div>

            {/* Social Media Icons */}
            <div className="flex space-x-4 mt-6 justify-center">
              <a href="#" className="text-amber-700 hover:text-amber-900 transition duration-300" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com/thegulmohargrand?igsh=MWJydjdicDQ4a3RqYw==" className="text-amber-700 hover:text-amber-900 transition duration-300" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="#" className="text-amber-700 hover:text-amber-900 transition duration-300" aria-label="Twitter">
                <Twitter size={20} />
              </a>
              <a href="#" className="text-amber-700 hover:text-amber-900 transition duration-300" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;